import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Edit,
  Check,
  X,
  Receipt,
  Printer,
  Ban,
  Download,
  Loader2,
} from 'lucide-react';
import Layout from '../components/Layout';
import purchaseService from '../services/purchaseService';
import authService from '../services/authService';
import PurchaseApprovalBadge from '../components/PurchaseApprovalBadge';
import PurchaseApprovalPanel from '../components/PurchaseApprovalPanel';
import PurchaseReceiptList from '../components/PurchaseReceiptList';
import PurchaseReceiptDialog from '../components/PurchaseReceiptDialog';
import FileAttachmentManager from '../components/FileAttachmentManager';
import BulkDownloadProductAttachmentsButton from '../components/BulkDownloadProductAttachmentsButton';
import TemplateSelector from '../components/TemplateSelector';
import excelExportService from '../services/excelExportService';

const PurchaseDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const canViewPrices = authService.canViewPOPrices();
  const user = authService.getCurrentUser();
  const isAdmin = user?.role === 'admin';
  const [order, setOrder] = useState(location.state?.orderData || null);
  const [loading, setLoading] = useState(!location.state?.orderData);
  const [error, setError] = useState('');
  const [actionDialog, setActionDialog] = useState({ open: false, action: '', order: null, reason: '' });
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [receiptDialog, setReceiptDialog] = useState({ open: false });

  // Get client context from order if available
  const selectedClientId = order?.client_id || null;
  const selectedClient = order?.client_name ? { name: order.client_name } : null;

  const loadOrder = useCallback(async (force = false) => {
    if (!id) return;
    if (!force && order) return; // Skip if we already have order data, unless forced

    try {
      setLoading(true);
      setError('');

      // Add cache-busting timestamp when forcing reload
      const response = await purchaseService.getPurchaseOrder(id, force ? Date.now() : null);
      setOrder(response);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load purchase order details');
    } finally {
      setLoading(false);
    }
  }, [id, order]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleAction = (action) => {
    if (action === 'edit') {
      navigate(`/purchases/${id}/edit`);
    } else if (action === 'export') {
      setTemplateSelectorOpen(true);
    } else if (action === 'receipt') {
      // Open receipt dialog directly
      setReceiptDialog({ open: true });
    } else if (['approve', 'reject', 'cancel'].includes(action)) {
      setActionDialog({ open: true, action, order, reason: '' });
    }
  };

  const handleTemplateSelected = async (template) => {
    try {
      setError('');

      const { blob, filename } = await excelExportService.exportUsingTemplate('purchase', {
        template_id: template.template_id,
        purchase_order_ids: [id]
      });

      excelExportService.downloadBlob(blob, filename);
      console.log('Export completed:', filename);
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.error || '导出采购单失败');
    }
  };

  const handleConfirmAction = async () => {
    const { action, order, reason } = actionDialog;
    setActionDialog({ open: false, action: '', order: null, reason: '' });

    try {
      if (action === 'approve') {
        await purchaseService.approvePurchaseOrder(order._id);
        setOrder(prev => ({ ...prev, approval_status: 'approved', status: 'confirmed' }));
        setError('');
        alert('Purchase order approved successfully!');
      } else if (action === 'reject') {
        await purchaseService.rejectPurchaseOrder(order._id, reason);
        setOrder(prev => ({ ...prev, approval_status: 'rejected', status: 'cancelled' }));
        setError('');
        alert('Purchase order rejected successfully!');
      } else if (action === 'cancel') {
        await purchaseService.cancelPurchaseOrder(order._id);
        setOrder(prev => ({ ...prev, status: 'cancelled' }));
        setError('');
        alert('Purchase order cancelled successfully!');
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} purchase order`);
    }
  };

  const handleReceiptSubmit = async (receiptData) => {
    try {
      setError('');
      const response = await purchaseService.addReceipt(order._id, receiptData);

      // Use the updated order from API response directly
      if (response.order) {
        setOrder(response.order);
      } else {
        // Fallback: reload order to get updated data
        await loadOrder(true);
      }

      // Success notification
      alert('收货记录添加成功！');
    } catch (err) {
      console.error('Error adding receipt:', err);
      setError(err.response?.data?.error || '添加收货记录失败');
      throw err; // Re-throw to let dialog handle it
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'submitted': return 'bg-info/10 text-info border-info/30';
      case 'confirmed': return 'bg-success/10 text-success border-success/30';
      case 'cancelled': return 'bg-destructive/10 text-destructive border-destructive/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getFulfillmentStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'bg-muted text-muted-foreground';
      case 'partial': return 'bg-info/10 text-info border-info/30';
      case 'complete': return 'bg-success/10 text-success border-success/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status, type = 'status') => {
    if (type === 'approval') {
      switch (status?.toLowerCase()) {
        case 'pending': return '待审批';
        case 'approved': return '已批准';
        case 'rejected': return '已拒绝';
        default: return status || '待审批';
      }
    } else if (type === 'fulfillment') {
      switch (status?.toLowerCase()) {
        case 'pending': return '待收货';
        case 'partial': return '部分收货';
        case 'complete': return '已完成';
        default: return status || '待收货';
      }
    } else {
      switch (status?.toLowerCase()) {
        case 'draft': return '草稿';
        case 'submitted': return '已提交';
        case 'confirmed': return '已确认';
        case 'cancelled': return '已取消';
        default: return status || '草稿';
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-6 px-4">
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !order) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-6 px-4">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="采购订单详情" activeTab="purchases">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/purchases')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-foreground">
              {order?.purchase_order_id}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {(() => { const pendingApproval = (order?.approval_status ?? (order?.status === 'pending' ? 'pending' : undefined)) === 'pending'; return isAdmin && pendingApproval; })() && (
              <>
                <Button
                  variant="default"
                  className="bg-success hover:bg-success/90"
                  onClick={() => handleAction('approve')}
                >
                  <Check className="h-4 w-4 mr-2" />
                  批准
                </Button>
                <Button
                  variant="outline"
                  className="border-destructive text-destructive hover:bg-destructive/10"
                  onClick={() => handleAction('reject')}
                >
                  <X className="h-4 w-4 mr-2" />
                  拒绝
                </Button>
              </>
            )}
            {isAdmin && order?.status !== 'cancelled' && (
              <Button
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/10"
                onClick={() => handleAction('cancel')}
              >
                <Ban className="h-4 w-4 mr-2" />
                取消订单
              </Button>
            )}
            {order?.status === 'confirmed' && (
              <Button
                variant="outline"
                onClick={() => handleAction('receipt')}
                disabled={order?.approval_status !== 'approved'}
              >
                <Receipt className="h-4 w-4 mr-2" />
                添加收货记录
              </Button>
            )}
            <Button variant="outline" onClick={() => handleAction('edit')}>
              <Edit className="h-4 w-4 mr-2" />
              编辑
            </Button>
            <Button variant="secondary" onClick={() => handleAction('export')}>
              <Download className="h-4 w-4 mr-2" />
              导出Excel
            </Button>
            <BulkDownloadProductAttachmentsButton
              entityType="purchases"
              entityId={order._id}
              entityName={order.purchase_order_id}
            />
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />
              打印
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-2">订单信息</h2>
                <Separator className="mb-4" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">采购单编号</p>
                    <p className="font-medium">{order?.purchase_order_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">供应商</p>
                    <p className="font-medium">{order?.supplier_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">状态</p>
                    <Badge className={cn('border', getStatusClass(order?.status))}>
                      {getStatusLabel(order?.status)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">审批状态</p>
                    <PurchaseApprovalBadge
                      status={order?.approval_status}
                      size="small"
                    />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">收货状态</p>
                    <Badge className={cn('border', getFulfillmentStatusClass(order?.fulfillment_status))}>
                      {getStatusLabel(order?.fulfillment_status, 'fulfillment')}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">客户</p>
                    <p className="font-medium">{order?.client_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">创建日期</p>
                    <p>{formatDate(order?.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">预计交货日期</p>
                    <p>{formatDate(order?.expected_delivery_date) || '-'}</p>
                  </div>
                  {order?.payment_terms && (
                    <div>
                      <p className="text-sm text-muted-foreground">付款条款</p>
                      <p>{order.payment_terms}</p>
                    </div>
                  )}
                  {order?.currency && (
                    <div>
                      <p className="text-sm text-muted-foreground">币种</p>
                      <p>{order.currency}</p>
                    </div>
                  )}
                  {order?.comment && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">备注</p>
                      <p>{order.comment}</p>
                    </div>
                  )}
                  {(order?.sales_order_reference || order?.source_sales_order) && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">关联销售订单</p>
                      <button
                        className="text-primary underline hover:text-primary/80"
                        onClick={() => {
                          // Use source_sales_order.sales_order_object_id if available (new schema)
                          // Fall back to sales_order_id for older orders (old schema)
                          const salesOrderId = order?.source_sales_order?.sales_order_object_id || order?.sales_order_id;
                          if (salesOrderId) {
                            navigate(`/sales/${salesOrderId}/view`);
                          }
                        }}
                      >
                        {order.sales_order_reference || order?.source_sales_order?.sales_order_id}
                      </button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Products Table */}
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-2">产品明细</h2>
                <Separator className="mb-4" />
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="px-4 py-3 text-left font-medium">产品编码</th>
                        <th className="px-4 py-3 text-left font-medium">产品名称</th>
                        <th className="px-4 py-3 text-left font-medium">货号</th>
                        <th className="px-4 py-3 text-left font-medium">规格</th>
                        <th className="px-4 py-3 text-right font-medium">数量</th>
                        {canViewPrices && <th className="px-4 py-3 text-right font-medium">单价</th>}
                        {canViewPrices && <th className="px-4 py-3 text-right font-medium">小计</th>}
                        <th className="px-4 py-3 text-left font-medium">备注</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {order?.products?.map((product, index) => (
                        <tr key={index} className="hover:bg-muted/30">
                          <td className="px-4 py-3">{product.product_id || '-'}</td>
                          <td className="px-4 py-3">{product.product_name || product.name}</td>
                          <td className="px-4 py-3">{product.part_number || '-'}</td>
                          <td className="px-4 py-3">{product.specification || '-'}</td>
                           <td className="px-4 py-3 text-right">{product.quantity}</td>
                          {canViewPrices && <td className="px-4 py-3 text-right">{formatCurrency(product.unit_price || product.unit_cost)}</td>}
                          {canViewPrices && <td className="px-4 py-3 text-right">{formatCurrency(product.line_total || product.total_price)}</td>}
                          <td className="px-4 py-3">{product.notes || '-'}</td>
                        </tr>
                      ))}
                      {canViewPrices && (
                        <tr className="bg-muted/50">
                          <td colSpan={5} className="px-4 py-3 text-right font-semibold">小计:</td>
                          <td className="px-4 py-3 text-right font-semibold">
                            {formatCurrency(order?.subtotal)}
                          </td>
                          <td />
                        </tr>
                      )}
                      {canViewPrices && order?.tax_rate > 0 && (
                        <tr className="bg-muted/50">
                          <td colSpan={5} className="px-4 py-3 text-right text-sm">
                            税率 ({(order.tax_rate * 100).toFixed(1)}%):
                          </td>
                          <td className="px-4 py-3 text-right text-sm">
                            {formatCurrency(order?.tax_amount)}
                          </td>
                          <td />
                        </tr>
                      )}
                      {canViewPrices && (
                        <tr className="bg-muted/50">
                          <td colSpan={5} className="px-4 py-3 text-right text-lg font-semibold">总计:</td>
                          <td className="px-4 py-3 text-right text-lg font-bold text-primary">
                            {formatCurrency(order?.total_cost)}
                          </td>
                          <td />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* File Attachments */}
            <FileAttachmentManager
              entityType="purchases"
              entityId={order?._id}
              title="采购订单附件"
              className="mb-6"
            />
          </div>

          {/* Approval Panel & Summary Panel */}
          <div className="space-y-6">
            <PurchaseApprovalPanel
              order={order}
              userRole="admin" // This should come from authentication context
              onApprovalChange={(updatedOrder) => {
                setOrder(updatedOrder);
                setError('');
              }}
              showAnalytics={true}
            />
            <Card className="border bg-card">
              <CardContent className="pt-6">
                <h2 className="text-lg font-semibold mb-2">订单摘要</h2>
                <Separator className="mb-4" />
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">产品总数</span>
                    <span className="text-sm">{order?.total_products || 0} 项</span>
                  </div>
                  {canViewPrices && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">总金额</span>
                        <span className="font-semibold">{formatCurrency(order?.total_cost)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">已收货金额</span>
                        <span className="text-sm">{formatCurrency(order?.received_total || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">待收货金额</span>
                        <span className="text-sm text-warning">
                          {formatCurrency((order?.total_cost || 0) - (order?.received_total || 0))}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>


            {/* Receipt Management */}
            <div className="mt-6">
              <PurchaseReceiptList
                purchaseOrder={order}
                onReceiptChange={(updatedData) => {
                  // Refresh the order data when receipts change
                  if (updatedData && !updatedData.deleted) {
                    loadOrder();
                  }
                }}
                readOnly={order?.status === 'cancelled'}
              />
            </div>
          </div>
        </div>

        {/* Action Dialog */}
        <Dialog
          open={actionDialog.open}
          onOpenChange={(open) => !open && setActionDialog({ open: false, action: '', order: null, reason: '' })}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {actionDialog.action === 'approve' && '批准采购单'}
                {actionDialog.action === 'reject' && '拒绝采购单'}
                {actionDialog.action === 'cancel' && '取消采购单'}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                {actionDialog.action === 'approve' &&
                  `确定要批准采购单 ${order?.purchase_order_id} 吗？`
                }
                {actionDialog.action === 'reject' &&
                  `确定要拒绝采购单 ${order?.purchase_order_id} 吗？`
                }
                {actionDialog.action === 'cancel' &&
                  `确定要取消采购单 ${order?.purchase_order_id} 吗？此操作不可撤销。`
                }
              </p>
              {actionDialog.action === 'reject' && (
                <div className="mt-4 space-y-2">
                  <Label htmlFor="reject-reason">拒绝原因</Label>
                  <Textarea
                    id="reject-reason"
                    rows={4}
                    value={actionDialog.reason}
                    onChange={(e) => setActionDialog(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="请输入拒绝原因..."
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setActionDialog({ open: false, action: '', order: null, reason: '' })}
              >
                取消
              </Button>
              <Button
                onClick={handleConfirmAction}
                variant={actionDialog.action === 'reject' || actionDialog.action === 'cancel' ? 'destructive' : 'default'}
                disabled={actionDialog.action === 'reject' && !actionDialog.reason.trim()}
              >
                {actionDialog.action === 'approve' && '批准'}
                {actionDialog.action === 'reject' && '拒绝'}
                {actionDialog.action === 'cancel' && '取消'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Receipt Dialog */}
        <PurchaseReceiptDialog
          open={receiptDialog.open}
          onClose={() => setReceiptDialog({ open: false })}
          onSubmit={handleReceiptSubmit}
          purchaseOrder={order}
          selectedClientId={selectedClientId}
          selectedClient={selectedClient}
        />

        {/* Template Selector Dialog */}
        <TemplateSelector
          open={templateSelectorOpen}
          onClose={() => setTemplateSelectorOpen(false)}
          entityType="purchase"
          onSelect={handleTemplateSelected}
        />
      </div>
    </Layout>
  );
};

export default PurchaseDetailPage;
